import type { FirestoreTimestamp } from "@/firebase/schemas/types"
import type { TranslationFunction } from "@/contexts/LanguageContext"

// Covers every shape a "createdAt"-style field actually arrives in across this codebase:
// a real Firestore Timestamp (client or admin SDK, both have .toDate()), some other
// duck-typed "has a toDate()" object (e.g. callers with their own narrower Timestamp-like
// type), a plain Date (e.g. an optimistic evaluation created locally before being reloaded
// from Firestore), an ISO string or epoch-ms number (serialized through an API boundary),
// a serialized Firestore Timestamp (`{_seconds, _nanoseconds}` - what a raw admin Timestamp
// turns into once it round-trips through `res.json()` without an explicit `.toDate()` call
// on the server, e.g. `/api/admin/agentHealth` - confirmed live as the cause of "Invalid
// Date" on the Agents tab), or missing.
type DateLike =
  | FirestoreTimestamp
  | Date
  | { toDate: () => Date }
  | { _seconds: number; _nanoseconds?: number }
  | string
  | number
  | null
  | undefined

// Only used internally by the currency/percent formatters below - not exported on its own
// since every current call site is one of those three.
function coerceNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

// This is a single function with two call signatures (lines below with no body) plus one
// implementation (the one with a body) - a standard TS overload, not three separate
// functions. It exists so most callers keep the simple "always get a string back" contract,
// while a caller that explicitly wants to distinguish "no date" from "has a date" (e.g. to
// conditionally render a whole line only when there's something to show) can opt in with
// `fallback: null` and get `string | null` back instead.
function formatDate(value: DateLike, options: { locale?: string; fallback: null }): string | null
function formatDate(value: DateLike, options?: { locale?: string; fallback?: string }): string
function formatDate(value: DateLike, options?: { locale?: string; fallback?: string | null }): string | null {
  const { locale = "en-US", fallback = "Unknown date" } = options ?? {}
  if (value === null || value === undefined) return fallback

  let date: Date
  if (value instanceof Date) {
    date = value
  } else if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    date = value.toDate()
  } else if (typeof value === "object" && "_seconds" in value && typeof value._seconds === "number") {
    date = new Date(value._seconds * 1000)
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value)
  } else {
    return fallback
  }

  if (Number.isNaN(date.getTime())) return fallback

  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(
    date
  )
}

/**
 * "Just now" / "N minutes ago" / "N hours ago" for anything under a day old; falls back to
 * `formatDate`'s absolute format for anything older. Needs the app's own `t()` for the relative
 * strings (translation keys: `messages.relative.justNow`/`minutesAgo`/`hoursAgo`) - this is the
 * one formatter here that isn't translation-agnostic, since relative time inherently is.
 */
function formatRelativeDate(millis: number, t: TranslationFunction, options?: { locale?: string }): string {
  if (!millis) return ""
  const delta = Date.now() - millis
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour

  if (delta < minute) return t("messages.relative.justNow")
  if (delta < hour) return t("messages.relative.minutesAgo", { count: Math.round(delta / minute) })
  if (delta < day) return t("messages.relative.hoursAgo", { count: Math.round(delta / hour) })

  return formatDate(millis, { locale: options?.locale })
}

export const formatter = {
  propertyDescription: (html: string) => {
    const withTagsConverted = html
      // <li><p>...</p></li> is the common nested shape in this data - handle it as one
      // bullet line before the generic <p>/<li> rules below would otherwise split it
      // across two lines.
      .replace(/<li[^>]*>\s*<p[^>]*>/gi, "\n- ")
      .replace(/<\/p>\s*<\/li>/gi, "\n")
      .replace(/<li[^>]*>/gi, "\n- ")
      .replace(/<\/li>/gi, "\n")
      .replace(/<\/?(p|ul|ol|div)[^>]*>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      // Strip any remaining tags (strong, b, em, i, u, span, a, ...) but keep their text.
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&(#39|apos);/gi, "'")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")

    // Final normalization pass, deliberately NOT another chained regex: source text has
    // shown up with all sorts of "blank line" flavors on top of the tags above - repeated
    // \r\n (Windows line endings), long runs of bare \n, whitespace-only lines, non-breaking
    // spaces - any of which can slip past a regex tuned for one specific pattern and leave
    // a huge visible gap. Splitting into lines, trimming each one (including non-breaking spaces), and
    // dropping every empty line before rejoining makes "at most one blank line between
    // paragraphs" unconditional, regardless of what produced the whitespace.
    return withTagsConverted
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .map((line) => line.replace(/[ \t ]+/g, " ").trim())
      .filter((line) => line.length > 0)
      .join("\n\n")
  },

  /**
   * Formats a Firestore Timestamp / Date / ISO string / epoch-ms number into a localized
   * "Sep 17, 2026, 3:45 PM"-style string. Returns `fallback` (default: "Unknown date") for
   * missing or unparseable input; pass `fallback: null` to get `null` back instead (e.g. to
   * conditionally skip rendering a "Last Update: ..." line entirely).
   */
  date: formatDate,

  /** "Just now" / "N minutes/hours ago", falling back to `date` for anything a day old or
   * older - see `formatRelativeDate`'s own doc comment. */
  relativeDate: formatRelativeDate,

  /** Trims a value to a plain string, coercing non-strings; `null`/`undefined` become "". */
  internalId: (value: unknown): string => {
    if (value === null || value === undefined) return ""
    const raw = typeof value === "string" ? value : String(value)
    return raw.trim()
  },

  /** Portugal-format postal code mask: "NNNN-NNN". Truncates/ignores non-digit input. */
  postalCodePT: (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 7)
    if (digits.length <= 4) return digits
    return `${digits.slice(0, 4)}-${digits.slice(4)}`
  },

  /** EUR currency, no decimals. Returns "-" for missing/non-numeric input. */
  currencyEUR: (value: unknown): string => {
    const num = coerceNumber(value)
    if (num === null) return "-"
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(num)
    } catch {
      return `€${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    }
  },

  /** EUR currency per square meter, no decimals. Returns "-" for missing/non-numeric input. */
  currencyPerSqmEUR: (value: unknown): string => {
    const num = coerceNumber(value)
    if (num === null) return "-"
    try {
      return `${new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(num)} /m²`
    } catch {
      return `€${num.toLocaleString(undefined, { maximumFractionDigits: 0 })} /m²`
    }
  },

  /**
   * Percent with 1 decimal. Values <= 1 are treated as a fraction (0.15 -> "15.0%"); values
   * > 1 are treated as already being a whole percent (15 -> "15.0%"). Returns "-" for
   * missing/non-numeric input.
   */
  percent: (value: unknown): string => {
    const num = coerceNumber(value)
    if (num === null) return "-"
    const pct = num <= 1 ? num * 100 : num
    return `${pct.toFixed(1)}%`
  },

  /** "21.7% (12m)" style IRR figure - see `financials.irr` in `PropertyProspects.schema.ts`.
   * Returns "-" when there's no percentage to show. */
  irr: (irr: { percentage: number; months: number } | null | undefined): string => {
    if (!irr || typeof irr.percentage !== "number" || !Number.isFinite(irr.percentage)) return "-"
    return irr.months ? `${irr.percentage.toFixed(1)}% (${irr.months}m)` : `${irr.percentage.toFixed(1)}%`
  },

  /** Title-cases each word ("pedro longle" / "PEDRO LONGLE" -> "Pedro Longle") - auth
   * providers don't guarantee a consistent casing for `session.user.name`, so this normalizes
   * it for display (e.g. the dashboard greeting) regardless of how it arrived. Splits on
   * whitespace only, so hyphenated/apostrophe'd names keep their internal casing untouched
   * (e.g. "mary-jane" -> "Mary-jane", not "Mary-Jane") - good enough for a greeting, not a
   * full name-formatting library. Returns "" for empty/non-string input.
   */
  capitalizeWords: (value: string | null | undefined): string => {
    if (!value) return ""
    return value
      .trim()
      .split(/\s+/)
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
      .join(" ")
  }
}
